import './index.scss';

import React, {useState} from 'react';

const IconProcessing = () => (
	<div className="elp__icon elp__icon--processing">
		<div className="elp__spinner" />
	</div>
);

const IconSuccess = () => (
	<div className="elp__icon elp__icon--success">
		<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<polyline points="20 6 9 17 4 12" />
		</svg>
	</div>
);

const IconError = () => (
	<div className="elp__icon elp__icon--error">
		<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<circle cx="12" cy="12" r="10" />
			<line x1="12" y1="8" x2="12" y2="12" />
			<line x1="12" y1="16" x2="12.01" y2="16" />
		</svg>
	</div>
);

const IconEmail = () => (
	<div className="elp__icon elp__icon--needs-email">
		<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<rect x="2" y="4" width="20" height="16" rx="2" />
			<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
		</svg>
	</div>
);

const CONTENT = {
	processing: {
		icon: <IconProcessing />,
		heading: 'Completing your request\u2026',
		body: 'Please wait while we verify your identity and process your action.',
	},
	'success:deletion': {
		icon: <IconSuccess />,
		heading: 'Account deleted',
		body: 'Your account and all associated data have been permanently removed.',
		closeHint: 'You can safely close this tab. The window where you requested this action has been updated.',
	},
	'success:signin': {
		icon: <IconSuccess />,
		heading: 'Signed in successfully',
		body: 'Your identity has been verified.',
		closeHint: 'You can close this tab and continue in the window where you requested sign-in.',
	},
	'success:emailChange': {
		icon: <IconSuccess />,
		heading: 'Email address updated',
		body: 'Your sign-in email has been changed. Sign in to LinkSpace again using your new email address.',
		closeHint: 'You can close this tab and return to LinkSpace to sign in.',
	},
	'success:emailRevert': {
		icon: <IconSuccess />,
		heading: 'Sign-in email reset',
		body: 'Your LinkSpace sign-in email has been restored to your previous address.',
		closeHint: 'You can close this tab. If you are signed in elsewhere, sign in again with your restored email.',
	},
	'success:verifyEmail': {
		icon: <IconSuccess />,
		heading: 'Email verified',
		body: 'Your email address has been verified.',
		closeHint: 'You can close this tab and continue in LinkSpace.',
	},
	'success:extension': {
		icon: <IconSuccess />,
		heading: 'Signed in successfully',
		body: 'Your identity has been verified.',
		closeHint: 'You can close this tab and return to the LinkSpace extension.',
	},
	'error': {
		icon: <IconError />,
		heading: 'Something went wrong',
		body: null,
		closeHint: null,
	},
	'needs-email': {
		icon: <IconEmail />,
		heading: 'Confirm your email',
		body: 'Enter the email address that received this link to complete the action.',
	},
};

const EmailLinkPage = ({status, action, onEmailSubmit, errorText}) => {
	const [email, setEmail] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const key = status === 'success'
		? `success:${action ?? 'signin'}`
		: (status ?? 'processing');

	const content = CONTENT[key] ?? CONTENT.processing;

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!email.trim() || submitting) return;
		setSubmitting(true);
		await onEmailSubmit(email.trim());
		setSubmitting(false);
	};

	return (
		<div className="elp" role="main" aria-live="polite">
			<div className="elp__glow elp__glow--1" aria-hidden="true" />
			<div className="elp__glow elp__glow--2" aria-hidden="true" />

			<main className="elp__main">
				<div className="elp__card">
					{content.icon}

					<h1 className="elp__heading">{content.heading}</h1>

					{status === 'error' && (
						<p className="elp__body" style={{color: 'rgba(252, 165, 165, 0.85)'}}>
							{errorText || 'An unexpected error occurred. Please close this tab and try again from the Linkspace app.'}
						</p>
					)}

					{status !== 'error' && content.body && (
						<p className="elp__body">{content.body}</p>
					)}

					{status === 'needs-email' && (
						<form className="elp__form" onSubmit={handleSubmit}>
							<div className="elp__field">
								<input
									type="email"
									placeholder="your@email.com"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									autoComplete="email"
									autoFocus
									disabled={submitting}
									aria-label="Email address"
								/>
							</div>
							<button
								type="submit"
								className="elp__submit"
								disabled={submitting || !email.trim()}
							>
								{submitting ? 'Verifying\u2026' : 'Continue'}
							</button>
							<p className="elp__hint">
								Use the same address that received this link.
							</p>
						</form>
					)}

					{content.closeHint && (
						<p className="elp__close-hint">{content.closeHint}</p>
					)}
				</div>
			</main>
		</div>
	);
};

export default EmailLinkPage;
